/**
 * Helpers for app-issued session JWTs (external email-code users).
 *
 * Internal staff carry MSAL tokens (refreshed silently by MSAL); external users
 * carry a short-lived JWT the backend signs itself, renewed through the httpOnly
 * refresh-token cookie rather than MSAL (MDRS-556). These helpers let the auth layer treat an app token as a
 * valid session for its own lifetime instead of the MSAL 15-min staleness model.
 */
import { APP_JWT_ISSUER } from "../constants/auth";
import { getAccessTokenCookie } from "./cookies";

/** Must match the backend EXTERNAL_JWT_ISSUER. Single source: constants/auth. */
export { APP_JWT_ISSUER };

interface AppTokenClaims {
  iss?: string;
  iat?: number; // seconds since epoch
  exp?: number; // seconds since epoch
  email?: string;
  user_type?: string;
  oid?: string;
  sub?: string;
}

/** Decode a JWT payload without verifying (verification is the backend's job). */
const decodeJwt = (token: string): AppTokenClaims | null => {
  try {
    const payload = token.split(".")[1];
    if (!payload) return null;
    const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(json) as AppTokenClaims;
  } catch {
    return null;
  }
};

/** Is this token one we issued (vs an MSAL/Azure AD token)? */
export const isAppToken = (token: string | null): boolean => {
  if (!token) return false;
  return decodeJwt(token)?.iss === APP_JWT_ISSUER;
};

/**
 * Whether the current in-memory token is an app-issued token that hasn't
 * expired per its own `exp` claim.
 */
export const hasValidAppToken = (): boolean => {
  const token = getAccessTokenCookie();
  const claims = token ? decodeJwt(token) : null;
  if (!claims || claims.iss !== APP_JWT_ISSUER || !claims.exp) return false;
  return claims.exp * 1000 > Date.now();
};

/**
 * How long an app-issued token is valid for in total (`exp - iat`), or null
 * for a token we didn't issue. A duration rather than a deadline, so the
 * caller can count it from when the token arrived and ignore any skew between
 * this machine's clock and the server's.
 */
export const getAppTokenLifetimeMs = (token: string): number | null => {
  const claims = decodeJwt(token);
  if (!claims || claims.iss !== APP_JWT_ISSUER || !claims.exp || !claims.iat) {
    return null;
  }
  return (claims.exp - claims.iat) * 1000;
};

/** Stable id of the token's user (Azure AD `oid`, else `sub`/`email`). */
export const getTokenUserKey = (token: string): string | null => {
  const claims = decodeJwt(token);
  return claims?.oid || claims?.sub || claims?.email || null;
};

/** Stable id of the signed-in user from the current token. */
export const getCurrentUserKey = (): string | null => {
  const token = getAccessTokenCookie();
  return token ? getTokenUserKey(token) : null;
};
